import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

interface PhaseDistributionChartProps {
  data: Array<{ phase: string; count: number; percentage: number }>;
  title: string;
  height?: number;
}

// Color palette for phases
const PHASE_COLORS: Record<string, string> = {
  'Non PM-SHRI': '#94a3b8', // Slate
  'Phase 1': '#22c55e', // Green
  'Phase 2': '#3b82f6', // Blue
  'Phase 3': '#8b5cf6', // Violet
  'Phase 5': '#f59e0b', // Amber
  'Phase 7': '#ef4444', // Red
};

const PhaseDistributionChart: React.FC<PhaseDistributionChartProps> = ({
  data,
  title,
  height = 350,
}) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const chartData = data.map((item) => ({
    ...item,
    fill: PHASE_COLORS[item.phase] || '#6B1786',
  }));

  const total = chartData.reduce((sum, item) => sum + item.count, 0);
  const pmshriTotal = chartData
    .filter((item) => item.phase !== 'Non PM-SHRI')
    .reduce((sum, item) => sum + item.count, 0);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-800">{item.phase}</p>
          <p className="text-purple-600">
            Schools: {item.count.toLocaleString()}
          </p>
          <p className="text-gray-500 text-sm">
            {item.percentage.toFixed(1)}% of total
          </p>
        </div>
      );
    }
    return null;
  };

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
        <div className="flex items-center justify-center h-64 text-gray-400">
          No data available
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex justify-between items-start mb-4">
        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        <div className="text-right">
          <p className="text-sm text-gray-500">Total PM-SHRI Schools</p>
          <p className="text-2xl font-bold text-green-600">
            {pmshriTotal.toLocaleString()}
            <span className="text-sm font-normal text-gray-500 ml-1">
              ({((pmshriTotal / total) * 100).toFixed(1)}%)
            </span>
          </p>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 20, right: 30, left: 100, bottom: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal />
          <XAxis
            type="number"
            tick={{ fontSize: 12, fill: '#666' }}
            tickFormatter={(value) => value.toLocaleString()}
          />
          <YAxis
            type="category"
            dataKey="phase"
            tick={{ fontSize: 12, fill: '#666' }}
            width={90}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar
            dataKey="count"
            radius={[0, 4, 4, 0]}
            onMouseEnter={(_, index) => setActiveIndex(index)}
            onMouseLeave={() => setActiveIndex(null)}
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.fill}
                opacity={activeIndex === null || activeIndex === index ? 1 : 0.6}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Legend */}
      <div className="flex flex-wrap justify-center gap-4 mt-4">
        {chartData.map((item) => (
          <div key={item.phase} className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: item.fill }}
            ></div>
            <span className="text-sm text-gray-600">
              {item.phase}: {item.count.toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PhaseDistributionChart;
